import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import {
  NodeViewContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';
import { createLowlight, common } from 'lowlight';

const lowlight = createLowlight(common);

/** Common languages for the picker (highlight.js ids). "" = auto-detect. */
const CODE_LANGUAGES: { value: string; label: string }[] = [
  { value: '', label: 'Auto' },
  { value: 'bash', label: 'Bash' },
  { value: 'c', label: 'C' },
  { value: 'cpp', label: 'C++' },
  { value: 'csharp', label: 'C#' },
  { value: 'css', label: 'CSS' },
  { value: 'diff', label: 'Diff' },
  { value: 'go', label: 'Go' },
  { value: 'graphql', label: 'GraphQL' },
  { value: 'xml', label: 'HTML / XML' },
  { value: 'java', label: 'Java' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'json', label: 'JSON' },
  { value: 'kotlin', label: 'Kotlin' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'php', label: 'PHP' },
  { value: 'python', label: 'Python' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'rust', label: 'Rust' },
  { value: 'scss', label: 'SCSS' },
  { value: 'shell', label: 'Shell' },
  { value: 'sql', label: 'SQL' },
  { value: 'swift', label: 'Swift' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'yaml', label: 'YAML' },
];

/** Code block with a language picker (top-left) + hover "copy" (top-right).
 *  Keeps the editable `<pre><code>` (via NodeViewContent) so lowlight syntax
 *  highlighting and editing are unaffected. */
function CodeBlockView({ node, updateAttributes, editor }: NodeViewProps) {
  const [copied, setCopied] = useState(false);
  const language = (node.attrs.language as string | null) ?? '';
  return (
    <NodeViewWrapper className="group">
      {/* Header bar (not editable): language picker (left) + copy (right). Sits
          flush on top of the <pre> — no overlap with the code's first line. */}
      <div
        contentEditable={false}
        className="mt-2 flex items-center justify-between rounded-t-md border-b border-zinc-700 bg-zinc-800 px-2 py-1"
      >
        <select
          aria-label="Code language"
          value={language}
          disabled={!editor.isEditable}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => updateAttributes({ language: e.target.value || null })}
          className="cursor-pointer rounded bg-transparent py-0.5 pr-5 text-xs text-zinc-300 focus:outline-none disabled:cursor-default"
        >
          {CODE_LANGUAGES.map((l) => (
            <option key={l.value} value={l.value} className="bg-zinc-800">
              {l.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label="Copy code"
          title="Copy code"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            void navigator.clipboard?.writeText(node.textContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="cursor-pointer rounded p-1 text-zinc-400 opacity-0 transition-colors group-hover:opacity-100 hover:text-white focus:opacity-100"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </div>
      <pre className="!mt-0 !rounded-t-none">
        {/* `as` is typed narrowly upstream; a real <code> keeps lowlight + the
            `pre code` prose styles working. */}
        <NodeViewContent as={'code' as 'div'} />
      </pre>
    </NodeViewWrapper>
  );
}

/** Syntax-highlighted code block (lowlight) + copy button. */
export const CodeBlock = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockView);
  },
}).configure({ lowlight });
